import React, { memo } from 'react';
import { motion } from 'framer-motion';
import { AngerMonster, FearMonster, SadnessMonster, HappinessMonster, CalmMonster } from './Monsters';
const chapters = [{
  id: 'anger',
  title: 'Los Monstruos del Fuego',
  emotionEn: 'Anger / Mad',
  emotionEs: 'Enojo / Furia',
  color: 'bg-anger',
  lightColor: 'bg-anger/10',
  textColor: 'text-anger',
  Monster: AngerMonster,
  desc: 'El fuego no es malo, pero necesita un lugar para quemar sin lastimar a nadie. No necesitamos apagar el fuego a gritos; necesitamos dibujarlo.',
  insight: 'El enojo es energía que necesita moverse. Si el niño no la mueve con las manos (dibujando), la moverá con los pies o la boca.'
}, {
  id: 'fear',
  title: 'Los Monstruos de las Sombras',
  emotionEn: 'Fear / Scared',
  emotionEs: 'Miedo / Asustado',
  color: 'bg-fear',
  lightColor: 'bg-fear/10',
  textColor: 'text-fear',
  Monster: FearMonster,
  desc: 'Los monstruos de las sombras se hacen pequeños cuando les ponemos colores brillantes. Al nombrarlo en inglés y español, tú te haces más grande.',
  insight: 'No le digas "no tengas miedo". Dile "veo que tienes miedo, vamos a darle un color para que no esté tan oscuro".'
}, {
  id: 'sadness',
  title: 'Los Monstruos de la Lluvia',
  emotionEn: 'Sadness / Crying',
  emotionEs: 'Tristeza / Llorar',
  color: 'bg-sadness',
  lightColor: 'bg-sadness/10',
  textColor: 'text-sadness',
  Monster: SadnessMonster,
  desc: 'A veces las nubes grises nos visitan y nos hacen sentir pesados. Llorar es como la lluvia que limpia el cielo para que vuelva a salir el sol.',
  insight: 'La tristeza necesita ser sentida, no escondida. Dibujar las lágrimas ayuda a que fluyan y pasen más rápido.'
}, {
  id: 'happiness',
  title: 'Los Monstruos del Sol',
  emotionEn: 'Happiness / Joy',
  emotionEs: 'Felicidad / Alegría',
  color: 'bg-happiness',
  lightColor: 'bg-happiness/20',
  textColor: 'text-yellow-600',
  Monster: HappinessMonster,
  desc: 'Cuando sentimos cosquillas en la panza y ganas de saltar, el Monstruo del Sol está bailando con nosotros. ¡Usa tus colores más brillantes!',
  insight: 'Celebrar y nombrar las emociones positivas construye un banco de memoria feliz al que el niño puede recurrir en momentos difíciles.'
}, {
  id: 'calm',
  title: 'Los Monstruos del Viento',
  emotionEn: 'Calm / Peace',
  emotionEs: 'Calma / Paz',
  color: 'bg-calm',
  lightColor: 'bg-calm/10',
  textColor: 'text-calm',
  Monster: CalmMonster,
  desc: 'Respira profundo. El Monstruo del Viento es suave y silencioso. Nos enseña a ser como un árbol fuerte que se mueve con la brisa pero no se cae.',
  insight: 'La calma es una habilidad que se practica. Colorear suavemente y respirar ancla el sistema nervioso del niño.'
}];
export const ChapterPreviews = () => {
  return <section className="w-full py-24 px-6 bg-cream overflow-hidden" data-id="element-25">
      <div className="max-w-7xl mx-auto" data-id="element-26">
        <div className="text-center mb-16" data-id="element-27">
          <h2 className="font-display text-4xl md:text-5xl font-bold text-dark mb-4" data-id="element-28">
            Conoce a los Monstruos
          </h2>
          <p className="font-body text-xl text-dark/70" data-id="element-29">
            Meet the Monsters inside the book
          </p>
        </div>

        <div className="space-y-12" data-id="element-30">
          {chapters.map((chapter, index) => <motion.div key={chapter.id} initial={{
          opacity: 0,
          y: 40
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true,
          margin: '-100px'
        }} transition={{
          duration: 0.6
        }} className={`flex flex-col ${index % 2 === 1 ? 'md:flex-row-reverse' : 'md:flex-row'} bg-white rounded-3xl overflow-hidden shadow-soft`} data-id="element-31">
              {/* Illustration Side */}
              <div className={`w-full md:w-2/5 ${chapter.lightColor} p-12 flex items-center justify-center relative`} data-id="element-32">
                <div className="absolute top-4 left-4 right-4 flex justify-between font-display font-bold opacity-30 text-2xl uppercase tracking-widest" data-id="element-33">
                  <span data-id="element-34">{chapter.emotionEn.split('/')[0]}</span>
                  <span data-id="element-35">{chapter.emotionEs.split('/')[0]}</span>
                </div>
                <chapter.Monster className="w-48 h-48 md:w-64 md:h-64 z-10" data-id="element-36" />
              </div>

              {/* Content Side */}
              <div className="w-full md:w-3/5 p-8 md:p-12 flex flex-col justify-center" data-id="element-37">
                <h3 className="font-display text-3xl font-bold text-dark mb-2" data-id="element-38">
                  {chapter.title}
                </h3>
                <div className="flex flex-wrap gap-3 mb-6" data-id="element-39">
                  <span className={`px-4 py-1 rounded-full ${chapter.lightColor} ${chapter.textColor} font-bold font-body text-sm`} data-id="element-40">
                    {chapter.emotionEn}
                  </span>
                  <span className={`px-4 py-1 rounded-full ${chapter.lightColor} ${chapter.textColor} font-bold font-body text-sm`} data-id="element-41">
                    {chapter.emotionEs}
                  </span>
                </div>

                <p className="font-body text-lg text-dark/80 mb-8 leading-relaxed" data-id="element-42">
                  {chapter.desc}
                </p>

                <div className="bg-gray-50 border-l-4 border-dark/20 p-4 rounded-r-xl" data-id="element-43">
                  <p className="font-body text-sm text-dark/70 italic" data-id="element-44">
                    <strong className="text-dark not-italic block mb-1" data-id="element-45">
                      💡 Insight Clave:
                    </strong>
                    {chapter.insight}
                  </p>
                </div>
              </div>
            </motion.div>)}
        </div>
      </div>
    </section>;
};